import { Link } from "@tanstack/react-router"
import { useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@holigral/shared/ui/components/ui/table"
import { Badge } from "@holigral/shared/ui/components/ui/badge"
import { InputField, SelectField } from "@holigral/shared/ui/composed"
import AssignmentRowActions from "./AssignmentRowActions"
import StatusBadge from "./StatusBadge"
import { AssignmentStatus } from "./model"
import type { AssignmentListFilter } from "./model"
import { useGetAssignmentsQuery } from "./api"
import { useT } from "@/i18n"
import { FilterBar, TablePagination } from "@/base"
import { useGetBriefsQuery } from "@/brief"
import { useCurrentTenant } from "@/tenant"
import { formatDate, useDebounce } from "@/utils"
import type { Uuid } from "@/utils"

const PER_PAGE = 25

// The status/brief/page selections live in the URL (the embedding route
// validates them and passes them in); the respondent email live-search
// stays local.
export type AssignmentsTableSearch = {
  status?: AssignmentStatus
  briefId?: Uuid
  page?: number
}

type Props = {
  /**
   * Filter fields fixed by the embedding page (e.g. a brief detail page
   * passes its briefId); the corresponding filter control is hidden
   */
  readonly fixedFilter?: Partial<AssignmentListFilter>
  readonly search: AssignmentsTableSearch
  readonly onSearchChange: (partial: AssignmentsTableSearch) => void
}

export default function AssignmentsTable({
  fixedFilter,
  search,
  onSearchChange,
}: Props) {
  const { t } = useT("assignment")
  const { tenantSlug } = useCurrentTenant()
  const [emailSearch, setEmailSearch] = useState("")
  const debouncedEmail = useDebounce(emailSearch, {
    delay: "short",
    instantOnFalsy: true,
  })

  const showStatusFilter = fixedFilter?.status === undefined
  const showBriefFilter = fixedFilter?.briefId === undefined
  // An unset filter is null rather than the "all" sentinel, so the trigger
  // shows its placeholder — which is what names the field now that the
  // filter bar carries no visible labels
  const status = search.status ?? null
  const briefId = search.briefId ?? null

  const { data: briefs } = useGetBriefsQuery(false, { skip: !showBriefFilter })
  const { data, isLoading } = useGetAssignmentsQuery({
    page: search.page ?? 1,
    perPage: PER_PAGE,
    ...(search.status && { status: search.status }),
    ...(debouncedEmail && { respondentEmail: debouncedEmail }),
    ...(showBriefFilter && !!briefId && { briefId }),
    ...fixedFilter,
  })

  const statusOptions = [
    { label: t("filters.allStatuses"), value: "all" },
    ...Object.values(AssignmentStatus).map((value) => ({
      label: t(`status.${value}`),
      value,
    })),
  ]

  const briefOptions = [
    { label: t("filters.allBriefs"), value: "all" },
    ...(briefs ?? []).map((brief) => ({
      label: brief.name,
      value: brief.id,
    })),
  ]

  const rows = data?.items ?? []

  return (
    <div className="flex flex-col gap-4">
      <FilterBar>
        {showStatusFilter && (
          <SelectField
            name="statusFilter"
            className="w-44"
            placeholder={t("table.status")}
            options={statusOptions}
            value={status}
            onChange={(value: string) => {
              onSearchChange({
                status:
                  value === "all" ? undefined : (value as AssignmentStatus),
                page: undefined,
              })
            }}
          />
        )}
        {showBriefFilter && (
          <SelectField
            name="briefFilter"
            className="w-52"
            placeholder={t("table.brief")}
            options={briefOptions}
            value={briefId}
            onChange={(value: string) => {
              onSearchChange({
                briefId: value === "all" ? undefined : value,
                page: undefined,
              })
            }}
          />
        )}
        <InputField
          name="respondentSearch"
          type="search"
          // Full width below `sm`: a fixed 18rem overflows the page's
          // px-6 gutters on a 320px viewport instead of wrapping
          className="w-full sm:w-72"
          placeholder={t("filters.respondentPlaceholder")}
          value={emailSearch}
          onChange={(e) => {
            setEmailSearch(e.target.value)
            onSearchChange({ page: undefined })
          }}
        />
      </FilterBar>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("table.respondent")}</TableHead>
            <TableHead>{t("table.status")}</TableHead>
            <TableHead>{t("table.brief")}</TableHead>
            <TableHead>{t("table.sent")}</TableHead>
            <TableHead>{t("table.requester")}</TableHead>
            <TableHead>{t("table.lastReminder")}</TableHead>
            <TableHead>
              <span className="sr-only">{t("table.actions")}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((assignment) => (
            <TableRow key={assignment.id}>
              <TableCell>
                <span className="inline-flex items-center gap-2">
                  {assignment.respondent.email ?? "—"}
                  {assignment.isSelf && (
                    <Badge variant="outline">{t("table.self")}</Badge>
                  )}
                </span>
              </TableCell>
              <TableCell>
                <StatusBadge status={assignment.status} />
              </TableCell>
              <TableCell>
                {assignment.brief ? (
                  <Link
                    to="/t/$tenantSlug/briefs/$briefId"
                    params={{ tenantSlug, briefId: assignment.brief.id }}
                    className="underline underline-offset-3 hover:text-foreground"
                  >
                    {assignment.brief.name}
                  </Link>
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell>{formatDate("date", assignment.createdAt)}</TableCell>
              <TableCell>{assignment.createdBy?.email ?? "—"}</TableCell>
              <TableCell>
                {assignment.lastReminderAt
                  ? formatDate("short", assignment.lastReminderAt)
                  : "—"}
              </TableCell>
              <TableCell>
                <AssignmentRowActions assignment={assignment} />
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && !isLoading && (
            <TableRow>
              <TableCell
                colSpan={7}
                className="py-8 text-center text-muted-foreground"
              >
                {t("table.empty")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination total={data?.total ?? 0} perPage={PER_PAGE} />
    </div>
  )
}
